<template>
  <div>
    <v-card-text class="mt-3">
      <v-alert type="info" text dense class="mb-4">
        Optional (CR-10): fills the Y-1 or ACT column of PL, BS, PL_ECO and BS_ECO
        from FINREP and the Business Report (ML report). Import the archive file and
        the FX rates first. Rows without a mapping rule (statistical rows,
        transformation tables, CAPEX, OPEX, RISK) are not changed.
        The other tabs keep working as before.
      </v-alert>

      <v-radio-group v-model="period" row class="mt-0" @change="resetPreview">
        <v-radio :label="periodLabel('Y-1')" value="Y-1"></v-radio>
        <v-radio :label="periodLabel('ACT')" value="ACT"></v-radio>
      </v-radio-group>

      <v-row>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="finrepFile"
            accept=".xlsx,.xlsm"
            label="FINREP (EF01.01-03, EF02.00) -> PL, BS"
            prepend-icon="mdi-file-excel"
            show-size
            @change="resetPreview"
          ></v-file-input>
        </v-col>
        <v-col cols="12" md="6">
          <v-file-input
            v-model="brFile"
            accept=".xlsx,.xlsm"
            :label="brLabel"
            prepend-icon="mdi-file-excel"
            show-size
            @change="resetPreview"
          ></v-file-input>
        </v-col>
      </v-row>
      <div v-if="period === 'ACT'" class="text-caption text--secondary mb-2">
        PL_ECO ACT is not read from the ML report: it is a copy of PL ACT, with the
        operating lease (rental income) rows and 4160109013 always 0.
      </div>

      <v-progress-linear v-if="busy" indeterminate color="accent" class="my-2"></v-progress-linear>

      <v-alert v-for="(err, i) in errors" :key="'err' + i" type="error" text dense>
        {{ err }}
      </v-alert>

      <template v-if="preview">
        <v-alert :type="applied ? 'success' : 'info'" text dense>
          <span v-if="applied">Saved: {{ preview.summary.updated_rows }} archive rows updated.</span>
          <span v-else>Preview only - nothing is saved until you click Apply.</span>
          <span v-if="preview.fx && preview.fx.local_currency !== 'EUR'">
            EUR -> {{ preview.fx.local_currency }} with the {{ preview.fx.fx_period }} rate
            (1 {{ preview.fx.local_currency }} = {{ preview.fx.eur_per_lcy }} EUR).
          </span>
        </v-alert>

        <div class="mb-2">
          <v-chip small class="mr-1">Rules: {{ preview.summary.rules }}</v-chip>
          <v-chip small class="mr-1">Changed rows: {{ changedCount }}</v-chip>
          <v-chip small class="mr-1" :color="preview.summary.missing_targets ? 'warning' : ''">
            Target not in archive: {{ preview.summary.missing_targets }}
          </v-chip>
          <v-chip small :color="preview.summary.with_warnings ? 'warning' : ''">
            With warnings: {{ preview.summary.with_warnings }}
          </v-chip>
        </div>

        <v-row align="center" dense>
          <v-col cols="12" md="7">
            <v-btn-toggle v-model="typeFilter" dense mandatory>
              <v-btn small value="all">All</v-btn>
              <v-btn small value="pl">PL</v-btn>
              <v-btn small value="bilanca">BS</v-btn>
              <v-btn small value="pl_eco">PL_ECO</v-btn>
              <v-btn small value="bs_eco">BS_ECO</v-btn>
            </v-btn-toggle>
          </v-col>
          <v-col cols="12" md="5">
            <v-switch
              v-model="onlyIssues"
              dense
              hide-details
              class="mt-0"
              label="Only changes and warnings"
            ></v-switch>
          </v-col>
        </v-row>

        <v-data-table
          :headers="headers"
          :items="rows"
          :items-per-page="15"
          dense
          class="mt-2"
        >
          <template v-slot:item.kind="{ item }">
            <span :class="item.kind === 'rule' ? '' : 'text--secondary'">{{ kindLabel(item.kind) }}</span>
          </template>
          <template v-slot:item.current_value="{ item }">{{ fmt(item.current_value) }}</template>
          <template v-slot:item.new_value="{ item }">{{ fmt(item.new_value) }}</template>
          <template v-slot:item.difference="{ item }">
            <span :class="Math.abs(item.difference || 0) > 0.005 ? 'font-weight-bold' : ''">
              {{ fmt(item.difference) }}
            </span>
          </template>
          <template v-slot:item.warnings="{ item }">
            <span v-if="item.warnings && item.warnings.length" class="orange--text text--darken-2">
              {{ item.warnings.join(' ') }}
            </span>
          </template>
        </v-data-table>
      </template>
    </v-card-text>

    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn large :disabled="busy" @click.stop="$emit('close')">
        {{ translate('button_cancel') }}
      </v-btn>
      <v-btn large :disabled="busy || !canRun" @click.stop="run('preview')">
        Preview
      </v-btn>
      <v-btn
        large
        color="accent"
        :disabled="busy || !preview || applied || !changedCount"
        @click.stop="confirmApply = true"
      >
        Apply
      </v-btn>
    </v-card-actions>

    <v-dialog v-model="confirmApply" max-width="480">
      <v-card>
        <v-card-title>Apply import?</v-card-title>
        <v-card-text>
          {{ changedCount }} archive rows in the {{ periodLabel(period) }} column will be overwritten
          with the values from the preview. Manual changes to these rows will be lost.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="confirmApply = false">{{ translate('button_cancel') }}</v-btn>
          <v-btn color="accent" @click="confirmApply = false; run('apply')">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import api from '../api/lbt-api';

const KIND_LABELS = {
  rule: 'Rule',
  eco_zero: 'Not in ML mapping -> 0',
  pl_copy: 'Copy of PL ACT',
  eco_exception: 'Excluded from ECO -> 0',
};

const numberFormat = new Intl.NumberFormat('hr-HR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default {
  name: 'ArchiveInternalImport',
  props: {
    planning: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      period: 'ACT',
      finrepFile: null,
      brFile: null,
      busy: false,
      errors: [],
      preview: null,
      applied: false,
      typeFilter: 'all',
      onlyIssues: true,
      confirmApply: false,
      headers: [
        { text: 'Type', value: 'type' },
        { text: 'FINREP', value: 'finrep' },
        { text: 'Bilpos', value: 'bilpos' },
        { text: 'Description', value: 'description' },
        { text: 'Source', value: 'kind' },
        { text: 'Current', value: 'current_value', align: 'end' },
        { text: 'New', value: 'new_value', align: 'end' },
        { text: 'Difference', value: 'difference', align: 'end' },
        { text: 'Warnings', value: 'warnings', sortable: false },
      ],
    };
  },
  computed: {
    ...mapGetters('translations', [
      'translate',
    ]),
    canRun() {
      return !!(this.planning && this.period && (this.finrepFile || this.brFile));
    },
    brLabel() {
      return this.period === 'Y-1'
        ? 'Business Report (ML report) -> BS_ECO, PL_ECO'
        : 'Business Report (ML report) -> BS_ECO';
    },
    changedCount() {
      if (!this.preview) {
        return 0;
      }
      return this.preview.results.filter(r => this.isChange(r)).length;
    },
    rows() {
      if (!this.preview) {
        return [];
      }
      return this.preview.results.filter((r) => {
        if (this.typeFilter !== 'all' && r.type !== this.typeFilter) {
          return false;
        }
        if (this.onlyIssues) {
          return this.isChange(r) || (r.warnings && r.warnings.length > 0);
        }
        return true;
      });
    },
  },
  methods: {
    periodLabel(period) {
      if (!this.planning) {
        return period;
      }
      const year = Number(this.planning.year);
      const month = Number(this.planning.month);
      if (period === 'Y-1') {
        return `Y-1 (${year - 1} FY)`;
      }
      return `ACT (${year} M${month - 1})`;
    },
    isChange(r) {
      return r.new_value !== null && r.new_value !== undefined
        && Math.abs(r.difference || 0) > 0.0000005;
    },
    kindLabel(kind) {
      return KIND_LABELS[kind] || kind;
    },
    fmt(value) {
      if (value === null || value === undefined || value === '') {
        return '';
      }
      return numberFormat.format(value);
    },
    resetPreview() {
      this.preview = null;
      this.applied = false;
      this.errors = [];
    },
    async run(action) {
      this.busy = true;
      this.errors = [];
      try {
        const result = await api.archiveInternalImport(
          this.planning.id, action, this.period, this.finrepFile, this.brFile,
        );
        this.preview = result;
        this.errors = result.errors || [];
        this.applied = action === 'apply';
        if (this.applied) {
          this.$emit('notify', {
            type: result.status === 'success' ? 'success' : 'warning',
            text: `Archive ${this.periodLabel(this.period)}: ${result.summary.updated_rows} rows updated.`,
          });
        }
      } catch (e) {
        if (action === 'preview') {
          this.preview = null;
        }
        this.errors = (e.data && e.data.errors && e.data.errors.length) ? e.data.errors : [e.message];
      } finally {
        this.busy = false;
      }
    },
  },
};
</script>
